import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const FIELDS = [
  { key: "safety_stock", label: "Safety stock (pouches)", hint: "Overrides the calculated reorder point" },
  { key: "target_stock", label: "Target stock (pouches)", hint: "Overrides the target coverage" },
  { key: "safety_days", label: "Safety days", hint: "Days of stock to keep as buffer" },
  { key: "target_days", label: "Target days", hint: "Days of stock to build toward" },
  { key: "sales_velocity_override", label: "Velocity override (pouches/day)", hint: "Force a fixed velocity instead of computed" },
];

export default function PlannerOverrideDialog({ open, onOpenChange, sku }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !sku) return;
    setLoading(true);
    api.get(`/skus/${sku.id}/planner-overrides`)
      .then(({ data }) => setValues(data || {}))
      .finally(() => setLoading(false));
  }, [open, sku]);

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const clearAll = () => setValues({});

  const save = async () => {
    if (!sku) return;
    setSaving(true);
    try {
      const payload = {};
      for (const f of FIELDS) {
        const v = values[f.key];
        payload[f.key] = (v === "" || v === null || v === undefined) ? null : Number(v);
      }
      await api.put(`/skus/${sku.id}/planner-overrides`, payload);
      toast.success("Planner overrides saved");
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Planner overrides · {sku?.name}</DialogTitle>
          <DialogDescription>
            Leave a field blank to inherit the global planner rules. Values here take precedence for this SKU only.
          </DialogDescription>
        </DialogHeader>
        {loading ? <div className="p-4 text-muted-foreground">Loading…</div> : (
          <div className="space-y-3">
            {FIELDS.map(f => (
              <div key={f.key}>
                <Label className="overline text-[10px]">{f.label}</Label>
                <Input type="number" step="0.1"
                       value={values[f.key] ?? ""}
                       onChange={(e) => set(f.key, e.target.value)}
                       placeholder="inherit global"
                       className="mono"
                       data-testid={`planner-override-${f.key}`}/>
                <div className="text-[10px] text-muted-foreground mt-0.5">{f.hint}</div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={clearAll} data-testid="planner-override-clear">Clear all</Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="planner-override-save">
            {saving ? "Saving…" : "Save overrides"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
